import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * SLA templates — the pre-defined uptime/response tiers the contract-create SLA
 * section selects from. Endpoint + field names are lifted verbatim from the v4
 * backend (`GET /sla-templates/active`, legacy `getActiveSLATemplates`) so the
 * request hits unchanged. Only the active tiers are offered at contract time.
 */

/** One active SLA tier (`SLATemplate` on the backend). */
export interface SlaTemplate {
  sla_template_id: number;
  template_name: string;
  /** Uptime guarantee percentage, e.g. `99.9`. */
  uptime_guarantee: number;
  downtime_allowance_minutes: number;
  /** Default first-response target for a ticket, in minutes. */
  default_response_time: number;
  /** Default resolution target for a ticket, in minutes. */
  default_resolution_time: number;
  business_hours_only: boolean;
  is_active?: boolean;
}

export const slaTemplateKeys = {
  active: ['sla-templates', 'active'] as const,
};

/** `GET /sla-templates/active` — the active SLA tiers, or `[]` on empty. */
export function useActiveSlaTemplates(enabled = true) {
  return useQuery({
    queryKey: slaTemplateKeys.active,
    enabled,
    queryFn: async (): Promise<SlaTemplate[]> => {
      const res = await dataCentralApi.get<ResponseArray<SlaTemplate>>(
        '/sla-templates/active',
      );
      return res.data.results ?? [];
    },
  });
}
